import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
  DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
  DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import { TeamType } from "@redotech/redo-model/team";
import { TransferDistributionMode } from "@redotech/redo-model/wms/wms-constants";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { ChannelConnectionState } from "@redotech/redo-web/arbiter-components/settings-card/settings-card";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputProvider } from "@redotech/ui/form";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../app/auth";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { LoadTeamContext, TeamContext } from "../team";
import * as teamCss from "../team.module.css";
import { AddBalanceCreditCard } from "./outbound-labels/add-balance-credit";
import {
  BalanceSettings,
  balanceSettingsDefaultValue,
  balanceSettingsForm,
} from "./outbound-labels/balance-settings";
import {
  CarrierSettingsCard,
  carrierSettingsDefault,
  carrierSettingsForm,
  CarrierSettingsValue,
} from "./outbound-labels/carrier-settings-card";
import {
  CompetitiveSpreadSettingsCard,
  competitiveSpreadSettingsDefault,
  competitiveSpreadSettingsForm,
  redoCompetitiveSpreadSettingsDefault,
  redoCompetitiveSpreadSettingsForm,
} from "./outbound-labels/competitive-spread-settings";
import { ConnectTeamTo3plCard } from "./outbound-labels/connect-team-to-3pl";
import { CopyFulfillmentGroupViewsCard } from "./outbound-labels/copy-fulfillment-group-views-card";
import {
  CreditCardFeeSettingsCard,
  creditCardFeeSettingsDefault,
  creditCardFeeSettingsForm,
} from "./outbound-labels/credit-card-fee-settings";
import {
  FreightSettingsCard,
  freightSettingsDefault,
  freightSettingsForm,
} from "./outbound-labels/freight-settings-card";
import { OutboundLabelsGeneral } from "./outbound-labels/general";
import { ImportPackageLearningCsvCard } from "./outbound-labels/import-package-learning-csv-card";

import { LookupFulfillmentBreakdownCard } from "./outbound-labels/lookup-fulfillment-breakdown";
import { LookupOrderTimelineCard } from "./outbound-labels/lookup-order-timeline";
import { LookupRateDetailsCard } from "./outbound-labels/lookup-rate-details";
import {
  MerchantCarrierFeeInitialState,
  MerchantCarrierFeeSettingsCard,
  merchantCarrierFeeSettingsDefault,
  merchantCarrierFeeSettingsForm,
} from "./outbound-labels/merchant-carrier-fee-settings";
import { NetsuiteIntegrationCard } from "./outbound-labels/netsuite-integration";
import { RateCalculatorCard } from "./outbound-labels/oms-pricing-tool/oms-pricing-tool-card";
import { PersonalizedSlipsSettingsCard } from "./outbound-labels/personalized-slips-settings";
import {
  PointsProgramSettingsCard,
  pointsSettingsDefault,
  pointsSettingsForm,
} from "./outbound-labels/points-program-settings";
import {
  ProductSyncFieldsCard,
  productSyncFieldsDefault,
  productSyncFieldsForm,
} from "./outbound-labels/product-sync-fields-card";
import { OutboundLabelRateCardCard } from "./outbound-labels/rate-tables/rate-table-card";
import { RedoChildCarrierAccountsCard } from "./outbound-labels/redo-child-carrier-accounts";
import { RedoLandmarkGlobalIntegration } from "./outbound-labels/redo-landmark-global-integration";
import {
  WmsEnabledCard,
  wmsEnabledDefault,
  wmsEnabledForm,
} from "./outbound-labels/wms-enabled-card";

export const OutboundLabelsPage = memo(function OutboundLabelsPage() {
  const params = useParams();
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const handleSave = useHandler(() => {
    loadTeam();
  });

  const settingsLoad = useLoad<OutboundLabelValue | undefined>(async () => {
    if (!team) {
      return;
    }

    const carrierSettings: CarrierSettingsValue = {
      directToCarrierIntegrations: {
        ups:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.ups ?? true,
        fedex:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.fedex ?? false,
        usps:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.usps ?? true,
        dhl:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.dhl ?? false,
        canadaPost:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.canadaPost ?? false,
      },
      internationalBridge: {
        usps:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.internationalBridge?.usps ?? true,
      },
      canAddDirectUspsCarrierAccount:
        team.settings.outboundLabels?.canAddDirectUspsCarrierAccount ?? false,
      blockedServiceLevels:
        team.settings.outboundLabels?.blockedServiceLevels ?? [],
      dhlDirectIntegrationSettings: {
        pickupId:
          team.settings.outboundLabels?.dhlDirectIntegrationSettings
            ?.pickupId ?? "",
        distributionCenter:
          team.settings.outboundLabels?.dhlDirectIntegrationSettings
            ?.distributionCenter ?? "",
        useDefaultForAllLocations:
          team.settings.outboundLabels?.dhlDirectIntegrationSettings
            ?.useDefaultForAllLocations ?? false,
      },
      useAustraliaPostCarrierAccount:
        team.settings.outboundLabels?.useAustraliaPostCarrierAccount ?? false,
    };

    return {
      ...outboundLabelDefault,
      newOmsEnabled: team.settings.outboundLabels?.newOmsEnabled ?? false,
      routingRulesEnabled:
        team.settings.outboundLabels?.routingRulesEnabled ?? false,
      personalizedSlipsEnabled:
        team.settings.outboundLabels?.personalizedSlipsEnabled ?? false,
      automationDelaySeconds: (
        team.settings.outboundLabels?.automationDelaySeconds ?? 0
      ).toFixed(0),
      binNamespace: team.settings.outboundLabels?.binNamespace ?? "",
      binKey: team.settings.outboundLabels?.binKey ?? "",
      carrierSettings,
      balanceSettings: {
        minimumBalance:
          team?.settings?.outboundLabels?.billing?.autoReload?.floor ?? 0,
      },
      competitiveSpreadSettings: {
        enabled:
          team.settings.outboundLabels?.competitiveSpreadSettings?.enabled ??
          true,
        beatRateBy:
          team.settings.outboundLabels?.competitiveSpreadSettings?.beatRateBy ??
          DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
        minBeatRateBy:
          team.settings.outboundLabels?.competitiveSpreadSettings
            ?.minBeatRateBy ?? DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
        carrierSpreadOverrides:
          team.settings.outboundLabels?.competitiveSpreadSettings?.carrierSpreadOverrides?.map(
            (o) => ({
              carriers: o.carriers,
              minBeatRateBy: o.minBeatRateBy,
              maxBeatRateBy: o.maxBeatRateBy,
            }),
          ) ?? DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
        minSpreadFloor:
          team.settings.outboundLabels?.competitiveSpreadSettings
            ?.minSpreadFloor ?? 0,
        enabledCarriers:
          team.settings.outboundLabels?.competitiveSpreadSettings
            ?.enabledCarriers ?? [],
        applyMerchantCompSpreadWhenTied:
          team.settings.outboundLabels?.competitiveSpreadSettings
            ?.applyMerchantCompSpreadWhenTied ?? false,
      },
      redoCompetitiveSpreadSettings: {
        enabled:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.enabled ?? true,
        beatRateBy:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.beatRateBy ?? DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
        minBeatRateBy:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.minBeatRateBy ?? DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
        carrierSpreadOverrides:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings?.carrierSpreadOverrides?.map(
            (o) => ({
              carriers: o.carriers,
              minBeatRateBy: o.minBeatRateBy,
              maxBeatRateBy: o.maxBeatRateBy,
            }),
          ) ?? DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
        minSpreadDifference:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.minSpreadDifference ?? 0,
        enabledCarriers:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.enabledCarriers ?? [],
      },
      pointsSettings: {
        enabled: team.settings.outboundLabels?.pointsSettings?.enabled ?? false,
        uspsLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.uspsLabelPointsAwarded ?? 5,
        fedexLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.fedexLabelPointsAwarded ?? 5,
        upsLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings?.upsLabelPointsAwarded ??
          5,
        amazonLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.amazonLabelPointsAwarded ?? 5,
        firstMileLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.firstMileLabelPointsAwarded ?? 5,
      },

      fees: {
        merchantCarrierAccountLabelFee: {
          amount:
            team.settings.outboundLabels?.fees?.merchantCarrierAccountLabelFee
              ?.amount ?? 0,
          tiktokShippingAmount:
            team.settings.outboundLabels?.fees?.merchantCarrierAccountLabelFee
              ?.tiktokShippingAmount ?? 0,
        },
        monthlySubscriptionFee: {
          amount:
            team.settings.outboundLabels?.fees?.monthlySubscriptionFee
              ?.amount ?? 0,
          turnedOnAt:
            team.settings.outboundLabels?.fees?.monthlySubscriptionFee
              ?.turnedOnAt ?? undefined,
        },
        wmsSubscriptionFee: {
          amount:
            team.settings.outboundLabels?.wms?.fees?.monthlySubscriptionFee
              ?.amount ?? 0,
          turnedOnAt:
            team.settings.outboundLabels?.wms?.fees?.monthlySubscriptionFee
              ?.turnedOnAt ?? undefined,
        },
      },
      chargeCreditCardProcessingFee:
        team.settings.outboundLabels?.fees?.chargeCreditCardProcessingFee ??
        false,
      wmsEnabled: {
        enabled: team.settings.outboundLabels?.wms?.enabled ?? false,
        packingParcelsEnabled:
          team.settings.outboundLabels?.wms?.packingParcelsEnabled ?? false,
        binCoordinateMonitoringEnabled:
          team.settings.outboundLabels?.wms?.binCoordinateMonitoring?.enabled ??
          false,
        binCoordinateMonitoringAlertThresholdPercent:
          team.settings.outboundLabels?.wms?.binCoordinateMonitoring
            ?.alertThresholdPercent ?? 80,
        sourceOfTruthEnabled:
          team.settings.outboundLabels?.wms?.sourceOfTruthEnabled ?? false,
        inferBinFromSku:
          team.settings.outboundLabels?.wms?.inferBinFromSku ?? false,
        processingAutoReceive:
          team.settings.outboundLabels?.wms?.processingAutoReceive ?? false,
        inventoryDriftMonitoringLocationIds:
          team.settings.outboundLabels?.wms
            ?.inventoryDriftMonitoringLocationIds ?? [],
        transferDistributionMode:
          team.settings.outboundLabels?.wms?.transferDistributionMode ??
          TransferDistributionMode.BIN_FIRST,
      },
      productSyncFields: {
        tags:
          team.settings.outboundLabels?.productUpdateSyncFields?.tags ?? false,
        title:
          team.settings.outboundLabels?.productUpdateSyncFields?.title ?? false,
        sku:
          team.settings.outboundLabels?.productUpdateSyncFields?.sku ?? false,
      },
      freightSettings: {
        freightEnabled:
          team.settings.outboundLabels?.freightSettings?.freightEnabled ?? true,
        spreadPercentage:
          team.settings.outboundLabels?.freightSettings?.spreadPercentage ?? 12,
      },
    };
  }, [team]);

  return (
    <OutboundLabelsForm
      initialValue={settingsLoad.value}
      key={+!team}
      onSave={handleSave}
      teamId={teamId}
    />
  );
});

export const outboundLabelForm = groupInput({
  newOmsEnabled: input<boolean>(),
  routingRulesEnabled: input<boolean>(),
  personalizedSlipsEnabled: input<boolean>(),
  automationDelaySeconds: input<string>(),
  binNamespace: input<string>(),
  binKey: input<string>(),
  carrierSettings: carrierSettingsForm,
  balanceSettings: balanceSettingsForm,
  competitiveSpreadSettings: competitiveSpreadSettingsForm,
  redoCompetitiveSpreadSettings: redoCompetitiveSpreadSettingsForm,
  pointsSettings: pointsSettingsForm,
  fees: merchantCarrierFeeSettingsForm,
  chargeCreditCardProcessingFee: creditCardFeeSettingsForm,
  wmsEnabled: wmsEnabledForm,
  productSyncFields: productSyncFieldsForm,
  freightSettings: freightSettingsForm,
});

export type OutboundLabelForm = InputProvider.Form<typeof outboundLabelForm>;

export type OutboundLabelValue = InputProvider.Value<typeof outboundLabelForm>;

export const outboundLabelDefault: OutboundLabelValue = {
  newOmsEnabled: false,
  routingRulesEnabled: false,
  personalizedSlipsEnabled: false,
  automationDelaySeconds: "0",
  binNamespace: "",
  binKey: "",
  carrierSettings: carrierSettingsDefault,
  balanceSettings: balanceSettingsDefaultValue,
  competitiveSpreadSettings: competitiveSpreadSettingsDefault,
  redoCompetitiveSpreadSettings: redoCompetitiveSpreadSettingsDefault,
  pointsSettings: pointsSettingsDefault,
  fees: merchantCarrierFeeSettingsDefault,
  chargeCreditCardProcessingFee: creditCardFeeSettingsDefault,
  wmsEnabled: wmsEnabledDefault,
  productSyncFields: productSyncFieldsDefault,
  freightSettings: freightSettingsDefault,
};

const omsEngineersBase = [
  "joe",
  "pj",
  "brian",
  "jaden.sterner",
  "jaden",
  "tyson.peterson",
  "tyson",
  "tyler.trommlitz",
  "coleman",
  "bcarper",
  "paul",
  "kyle",
  "dev",
];

export const omsEngineers = omsEngineersBase.flatMap((name) => [
  `${name}@getredo.com`,
  `${name}@redo.com`,
]);

const usersWhoCanEditRateTablesBase = [
  ...omsEngineersBase,
  "suz",
  "aaron",
  "jackie",
  "ridge",
  "hayden",
  "boston",
];

export const usersWhoCanEditRateTables = usersWhoCanEditRateTablesBase.flatMap(
  (name) => [`${name}@getredo.com`, `${name}@redo.com`],
);

export const usersWhoCanEditMerchantFeesBase = [
  ...usersWhoCanEditRateTablesBase,
  "spencer",
];

export const usersWhoCanEditMerchantFees =
  usersWhoCanEditMerchantFeesBase.flatMap((name) => [
    `${name}@getredo.com`,
    `${name}@redo.com`,
  ]);

const usersWhoCanSeeRateTablesBase = [
  ...usersWhoCanEditRateTablesBase,
  "patrick",
  "jordan",
  "hayden",
  "spencer",
  "logan",
  "eric",
  "boston",
  "dominic",
  "bryce",
];

const usersWhoCanSeeRateTables = usersWhoCanSeeRateTablesBase.flatMap(
  (name) => [`${name}@getredo.com`, `${name}@redo.com`],
);

const usersWhoCanChangeBalanceBase = [
  ...omsEngineersBase,
  "dev",
  "suz",
  "aaron",
  "hayden",
  "jackson",
  "aden",
  "kash",
  "josh.thayne",
  "ryan.merrill",
  "dallin.orchard",
  "bryce",
];

const usersWhoCanChangeBalance = usersWhoCanChangeBalanceBase.flatMap(
  (name) => [`${name}@getredo.com`, `${name}@redo.com`],
);

const usersWhoCanTurnOffCreditCardFeeBase = [
  ...omsEngineersBase,
  "spencer",
  "jackie",
  "hayden",
  "suz",
];

const usersWhoCanTurnOffCreditCardFee =
  usersWhoCanTurnOffCreditCardFeeBase.flatMap((name) => [
    `${name}@getredo.com`,
    `${name}@redo.com`,
  ]);

const usersWhoCanEditCompSpreadRangesBase = [
  ...omsEngineersBase,
  "suz",
  "jackie",
  "hayden",
];

export const usersWhoCanEditCompSpreadRanges =
  usersWhoCanEditCompSpreadRangesBase.flatMap((name) => [
    `${name}@getredo.com`,
    `${name}@redo.com`,
  ]);

const OutboundLabelsForm = memo(function OutboundLabelsForm({
  onSave,
  teamId,
  initialValue,
}: {
  teamId: string;
  initialValue: OutboundLabelValue | undefined;
  onSave?: (value: OutboundLabelValue) => void;
}) {
  const client = useRequiredContext(RedoAdminClientContext);
  const input = useInput(
    outboundLabelForm,
    initialValue || outboundLabelDefault,
  );
  const auth = useRequiredContext(AuthContext);
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const userCanUseBalance = useMemo(() => {
    return usersWhoCanChangeBalance.includes(auth.email);
  }, [auth]);

  const userCanEditRateTables = useMemo(() => {
    return usersWhoCanEditRateTables.includes(auth.email);
  }, [auth]);

  const userCanSeeRateTables = useMemo(() => {
    return usersWhoCanSeeRateTables.includes(auth.email);
  }, [auth]);

  const userCanTurnOffCreditCardFee = usersWhoCanTurnOffCreditCardFee.includes(
    auth.email,
  );

  const userCanEditCompSpreadRanges = useMemo(() => {
    return usersWhoCanEditCompSpreadRanges.includes(auth.email);
  }, [auth]);

  const isOmsEngineer = useMemo(() => {
    return omsEngineers.includes(auth.email);
  }, [auth]);

  const merchantFeeInitialState: MerchantCarrierFeeInitialState = useMemo(
    () => ({
      omsCanBeEnabled:
        !initialValue?.fees.monthlySubscriptionFee.turnedOnAt ||
        initialValue?.fees.monthlySubscriptionFee.amount <= 0,
      wmsCanBeEnabled:
        !initialValue?.fees.wmsSubscriptionFee.turnedOnAt ||
        initialValue?.fees.wmsSubscriptionFee.amount <= 0,
      initialOmsFee: initialValue?.fees.monthlySubscriptionFee.amount ?? 0,
      initialWmsFee: initialValue?.fees.wmsSubscriptionFee.amount ?? 0,
      initialLabelFee:
        initialValue?.fees.merchantCarrierAccountLabelFee.amount ?? 0,
      initialTiktokLabelFee:
        initialValue?.fees.merchantCarrierAccountLabelFee
          .tiktokShippingAmount ?? 0,
      initialOmsTurnedOnAt:
        initialValue?.fees.monthlySubscriptionFee.turnedOnAt,
      initialWmsTurnedOnAt: initialValue?.fees.wmsSubscriptionFee.turnedOnAt,
    }),
    [initialValue],
  );

  const initialSotEnabled =
    initialValue?.wmsEnabled.sourceOfTruthEnabled ?? false;
  const [showWmsOnboardConfirmation, setShowWmsOnboardConfirmation] =
    useState(false);
  const pendingSaveSignal = useRef<AbortSignal | null>(null);

  const [
    redoLandmarkGlobalConnectionState,
    setRedoLandmarkGlobalConnectionState,
  ] = useState<ChannelConnectionState>(ChannelConnectionState.NOT_CONNECTED);

  useLoad(
    async (signal) => {
      const response = await rpcClient.getRedoLandmarkGlobalIntegration(
        { teamId },
        { signal },
      );
      setRedoLandmarkGlobalConnectionState(
        response.exists
          ? ChannelConnectionState.CONNECTED
          : ChannelConnectionState.NOT_CONNECTED,
      );
      return response;
    },
    [teamId, rpcClient],
  );

  useEffect(() => {
    if (initialValue) {
      input.setValue(initialValue);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const wmsSotToggled =
    !initialSotEnabled && input.value.wmsEnabled.sourceOfTruthEnabled;

  const doSave: PageFormSave = useHandler(async (signal) => {
    const team = await getTeam(client, { teamId, signal });
    const value = input.value;

    team.settings.outboundLabels = {
      ...team.settings.outboundLabels,
      newOmsEnabled: value?.newOmsEnabled,
      routingRulesEnabled: value?.routingRulesEnabled,
      personalizedSlipsEnabled: value?.personalizedSlipsEnabled,
      useAustraliaPostCarrierAccount:
        value?.carrierSettings.useAustraliaPostCarrierAccount,
      automationDelaySeconds: Math.floor(
        parseFloat(value.automationDelaySeconds) || 0,
      ),
      binNamespace: value.binNamespace,
      binKey: value.binKey,
      blockedCarrierAccounts:
        team.settings.outboundLabels?.blockedCarrierAccounts ?? [],
      blockedServiceLevels: value.carrierSettings.blockedServiceLevels,
      canAddDirectUspsCarrierAccount:
        value.carrierSettings.canAddDirectUspsCarrierAccount,
      wms: {
        ...team.settings.outboundLabels?.wms,
        enabled: value.wmsEnabled.enabled,
        packingParcelsEnabled: value.wmsEnabled.packingParcelsEnabled,
        sourceOfTruthEnabled: value.wmsEnabled.sourceOfTruthEnabled,
        inferBinFromSku: value.wmsEnabled.inferBinFromSku,
        processingAutoReceive: value.wmsEnabled.processingAutoReceive,
        inventoryDriftMonitoringLocationIds:
          value.wmsEnabled.inventoryDriftMonitoringLocationIds,
        transferDistributionMode: value.wmsEnabled.transferDistributionMode,
        pickSession:
          team.settings.outboundLabels?.wms?.pickSession ?? undefined,
        fees: {
          monthlySubscriptionFee: {
            amount: value.fees.wmsSubscriptionFee.amount,
            turnedOnAt: value.fees.wmsSubscriptionFee.turnedOnAt,
          },
        },
        binCoordinateMonitoring: {
          enabled: value.wmsEnabled.binCoordinateMonitoringEnabled,
          alertThresholdPercent:
            value.wmsEnabled.binCoordinateMonitoringAlertThresholdPercent,
        },
      },
      enabledCarrierAccounts: {
        ...team.settings.outboundLabels?.enabledCarrierAccounts,
        directToCarrierIntegrations: {
          ...team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations,
          ups: value.carrierSettings.directToCarrierIntegrations.ups,
          fedex: value.carrierSettings.directToCarrierIntegrations.fedex,
          usps: value.carrierSettings.directToCarrierIntegrations.usps,
          dhl: value.carrierSettings.directToCarrierIntegrations.dhl,
          canadaPost:
            value.carrierSettings.directToCarrierIntegrations.canadaPost,
        },
        internationalBridge: {
          ...team.settings.outboundLabels?.enabledCarrierAccounts
            ?.internationalBridge,
          usps: value.carrierSettings.internationalBridge.usps,
        },
      },
      dhlDirectIntegrationSettings: {
        pickupId: value.carrierSettings.dhlDirectIntegrationSettings.pickupId,
        distributionCenter:
          value.carrierSettings.dhlDirectIntegrationSettings.distributionCenter,
        useDefaultForAllLocations:
          value.carrierSettings.dhlDirectIntegrationSettings
            .useDefaultForAllLocations,
      },
      billing: {
        ...team.settings.outboundLabels?.billing,
        autoReload: {
          amount:
            team?.settings?.outboundLabels?.billing?.autoReload?.amount ?? 0,
          threshold:
            team?.settings?.outboundLabels?.billing?.autoReload?.threshold ?? 0,
          floor: value.balanceSettings.minimumBalance,
        },
      },
      competitiveSpreadSettings: {
        enabled: value.competitiveSpreadSettings.enabled,
        beatRateBy: Number(
          value.competitiveSpreadSettings.beatRateBy.toFixed(2),
        ),
        minBeatRateBy: Number(
          value.competitiveSpreadSettings.minBeatRateBy.toFixed(2),
        ),
        carrierSpreadOverrides:
          value.competitiveSpreadSettings.carrierSpreadOverrides.map((o) => ({
            carriers: o.carriers,
            minBeatRateBy: Number(o.minBeatRateBy.toFixed(2)),
            maxBeatRateBy: Number(o.maxBeatRateBy.toFixed(2)),
          })),
        minSpreadFloor: Number(
          value.competitiveSpreadSettings.minSpreadFloor.toFixed(2),
        ),
        enabledCarriers: value.competitiveSpreadSettings.enabledCarriers,
        applyMerchantCompSpreadWhenTied:
          value.competitiveSpreadSettings.applyMerchantCompSpreadWhenTied,
      },
      redoCompetitiveSpreadSettings: {
        enabled: value.redoCompetitiveSpreadSettings.enabled,
        beatRateBy: Number(
          value.redoCompetitiveSpreadSettings.beatRateBy.toFixed(2),
        ),
        minBeatRateBy: Number(
          value.redoCompetitiveSpreadSettings.minBeatRateBy.toFixed(2),
        ),
        carrierSpreadOverrides:
          value.redoCompetitiveSpreadSettings.carrierSpreadOverrides.map(
            (o) => ({
              carriers: o.carriers,
              minBeatRateBy: Number(o.minBeatRateBy.toFixed(2)),
              maxBeatRateBy: Number(o.maxBeatRateBy.toFixed(2)),
            }),
          ),
        minSpreadDifference: Number(
          value.redoCompetitiveSpreadSettings.minSpreadDifference.toFixed(2),
        ),
        enabledCarriers: value.redoCompetitiveSpreadSettings.enabledCarriers,
      },
      pointsSettings: {
        enabled: value.pointsSettings.enabled,
        uspsLabelPointsAwarded: value.pointsSettings.uspsLabelPointsAwarded,
        fedexLabelPointsAwarded: value.pointsSettings.fedexLabelPointsAwarded,
        upsLabelPointsAwarded: value.pointsSettings.upsLabelPointsAwarded,
        amazonLabelPointsAwarded: value.pointsSettings.amazonLabelPointsAwarded,
        firstMileLabelPointsAwarded:
          value.pointsSettings.firstMileLabelPointsAwarded,
      },
      fees: {
        merchantCarrierAccountLabelFee: {
          amount: value.fees.merchantCarrierAccountLabelFee.amount,
          tiktokShippingAmount:
            value.fees.merchantCarrierAccountLabelFee.tiktokShippingAmount,
        },
        monthlySubscriptionFee: {
          amount: value.fees.monthlySubscriptionFee.amount,
          turnedOnAt: value.fees.monthlySubscriptionFee.turnedOnAt,
        },
        chargeCreditCardProcessingFee: value.chargeCreditCardProcessingFee,
      },
      productUpdateSyncFields: {
        tags: value.productSyncFields.tags,
        title: value.productSyncFields.title,
        sku: value.productSyncFields.sku,
      },
      freightSettings: {
        freightEnabled: value.freightSettings.freightEnabled,
        spreadPercentage: value.freightSettings.spreadPercentage,
      },
    };

    // Disable carrier service level learning if competitive spread is enabled
    // as it interferes with selecting the adjusted rate
    if (
      value.competitiveSpreadSettings.enabled ||
      value.redoCompetitiveSpreadSettings.enabled
    ) {
      team.settings.outboundLabels.learnedPresets = {
        parcel: team.settings.outboundLabels.learnedPresets?.parcel ?? true,
        weight: team.settings.outboundLabels.learnedPresets?.weight ?? false,
        ["carrier-service-level"]: false,
      };
    }

    await updateTeam(client, { teamId, team, signal });

    await rpcClient.createDefaultFlowsAndTemplates(
      { teamId, category: "Oms" },
      { signal },
    );

    if (wmsSotToggled) {
      await rpcClient.onboardWms({ teamId });
    }

    onSave?.(value);
  });

  const save: PageFormSave = useHandler(async (signal) => {
    if (wmsSotToggled) {
      pendingSaveSignal.current = signal;
      setShowWmsOnboardConfirmation(true);
      return;
    }
    await doSave(signal);
  });

  const isFulfillmentService = useMemo(() => {
    return team.teamType === TeamType.FulfillmentService;
  }, [team]);

  return (
    <div className={teamCss.container}>
      <PageForm
        initial={initialValue || outboundLabelDefault}
        input={input}
        save={save}
      >
        <div className={gridCss.grid}>
          <div className={gridCss.span12}>
            {userCanSeeRateTables && (
              <>
                <div className={gridCss.span12}>
                  <OutboundLabelRateCardCard
                    readonly={!userCanEditRateTables}
                  />
                </div>
                <br />
                <div className={gridCss.span12}>
                  <RateCalculatorCard />
                </div>
              </>
            )}
          </div>
        </div>
        <br />
        <div className={gridCss.grid}>
          <div className={gridCss.span4}>
            <OutboundLabelsGeneral input={input} />
            <br />
            <PersonalizedSlipsSettingsCard input={input} />
            {userCanUseBalance && (
              <>
                <br />
                <AddBalanceCreditCard team={team} />
              </>
            )}
            <br />
            <BalanceSettings
              balanceSettingsInput={input.inputs.balanceSettings}
              team={team}
            />
            <br />
            <MerchantCarrierFeeSettingsCard
              initialState={merchantFeeInitialState}
              input={input}
            />
            <br />
            <CreditCardFeeSettingsCard
              canTurnOffCreditCardFee={userCanTurnOffCreditCardFee}
              input={input}
            />
            <br />
            <RedoLandmarkGlobalIntegration
              connectionState={redoLandmarkGlobalConnectionState}
              setConnectionState={setRedoLandmarkGlobalConnectionState}
            />
            <br />
            <ProductSyncFieldsCard input={input} />
            <br />
            <FreightSettingsCard input={input} isOmsEngineer={isOmsEngineer} />
          </div>
          <div className={gridCss.span8}>
            <div className={gridCss.grid}>
              <div className={gridCss.span6}>
                <LookupOrderTimelineCard />
              </div>
              <div className={gridCss.span6}>
                <LookupFulfillmentBreakdownCard />
              </div>
              <div className={gridCss.span6}>
                <LookupRateDetailsCard />
              </div>
              {isFulfillmentService && (
                <div className={gridCss.span6}>
                  <ConnectTeamTo3plCard />
                </div>
              )}
              <div className={gridCss.span6}>
                <CopyFulfillmentGroupViewsCard />
              </div>
            </div>
            <br />
            <div className={gridCss.span4}>
              <CarrierSettingsCard input={input} omsEngineers={omsEngineers} />
            </div>
            <br />
            <RedoChildCarrierAccountsCard />
            <br />
            <div className={gridCss.grid}>
              <div className={gridCss.span6}>
                <CompetitiveSpreadSettingsCard
                  canEditSpreadRanges={userCanEditCompSpreadRanges}
                  input={input}
                />
              </div>
              <div className={gridCss.span6}>
                <PointsProgramSettingsCard input={input} />
                <br />
                <WmsEnabledCard input={input} />
                <br />
                <ImportPackageLearningCsvCard teamId={teamId} />
              </div>
            </div>
            <br />
            <NetsuiteIntegrationCard />
          </div>
        </div>
      </PageForm>

      <RedoModal
        isOpen={showWmsOnboardConfirmation}
        modalSize="sm"
        onModalCloseRequested={() => {
          setShowWmsOnboardConfirmation(false);
          pendingSaveSignal.current = null;
        }}
        primaryButton={{
          text: "Yes, enable",
          onClick: async () => {
            setShowWmsOnboardConfirmation(false);
            await doSave(
              pendingSaveSignal.current ?? new AbortController().signal,
            );
            pendingSaveSignal.current = null;
          },
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setShowWmsOnboardConfirmation(false);
            pendingSaveSignal.current = null;
          },
        }}
        title="Enable WMS Source of Truth?"
      >
        <Text fontSize="sm">
          This will start the WMS onboarding workflow to populate bin-level
          inventory from existing location inventory and import bin assignments
          from product metafields. This process may take several minutes
          depending on the number of products.
        </Text>
      </RedoModal>
    </div>
  );
});
